import React, { useEffect, useState } from "react";
import { createStyles, makeStyles } from "@mui/styles";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { getActionItems } from "../layout/data";
import { useSelector } from "react-redux";
import { showToast } from "../../store/actions";
import { SmallTile } from "../Common/Tile";
import CommonLayout from "../Common/Layout";
import DashboardService from "../../service/dashboard";
import Last20Jobs from "./Last20Jobs";

const useStyles = makeStyles((theme) =>
  createStyles({
    paper: {
      padding: "16px",
      textAlign: "center",
      color: theme.palette.text.secondary,
      height: 120,
      lineHeight: "60px",
      position: "relative",
      cursor: "pointer",
      userSelect: "none",
      borderRadius: 0,
      "&:hover": {
        transform: "scale(1.02)",
      },
    },
    skeleton: {
      borderRadius: "5px",
    },
    boxActionContainer: {
      height: "100%",
      margin: 0,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    dashboardTileMain: {
      width: "180px",
      textAlign: "center",
      // borderRadius: "12px",
      margin: "10px 30px",
    }
  })
);

const Dashboard = ({ isSupplierDashboard }) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { user, permissions } = useSelector((state) => state);
  const role = user.currentUser?.Role_id;
  const [dashboardActions, setDashboardActions] = useState([]);
  const [data, setData] = useState({
    QUOTES: 0,
    OPEN_ORDERS: 0,
    ARTWORK_APPROVALS: 0,
    SAMPLE_APPROVALS: 0,
    COMPLETED: 0,
    TOTAL_JOBS: 0,
  });
  const [loading, setLoading] = useState(false);

  const UserName = `${user.currentUser.FirstName}`;

  const ActionGrid = () =>
    dashboardActions.map(
      (
        {
          title,
          icon,
          navigateTo,
          bgcolor,
          color,
          buttonBgColor,
          buttonColor,
          displayInGrid,
          displayChildrenInGrid,
          children,
        },
        i
      ) => {
        return displayInGrid ? (
          <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={i}>
            <SmallTile
              {...{
                loading,
                icon: icon,
                label: title,
                link: navigateTo,
                value: "",
                bgcolor,
                color,
                buttonBgColor,
                buttonColor,
                clickEvent: navigate,
              }}
            />
          </Grid>
        ) : (
          displayChildrenInGrid &&
            children &&
            children.length &&
            children.map(({ id, title, icon, navigateTo, bgcolor, color, buttonBgColor, buttonColor }, j) => (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={j} style={{ paddingLeft: "10px", paddingRight: "10px" }} data-id={id}>
                <SmallTile
                  {...{
                    loading,
                    icon: icon,
                    label: title,
                    link: navigateTo,
                    value: data[id],
                    bgcolor,
                    color,
                    buttonBgColor,
                    buttonColor,
                    clickEvent: navigate,
                  }}
                />
              </Grid>
            ))
        );
      }
    );

  useEffect(() => {
    (async () => {
      setLoading(true);

      const dashboardService = new DashboardService();
      const response = await dashboardService.getDashboardData(role, isSupplierDashboard);

      if (response.success) setData(response.data);

      setLoading(false);
    })();

    return () => setData([]);
  }, [isSupplierDashboard, role]);

  useEffect(() => {
    const roles = getActionItems(permissions, user.currentUser.isAdminOrEmp, role);
    if (!roles.length) {
      dispatch(
        showToast({
          message: "User does not have permission to access dashboard, please contact Admin.",
          type: "error",
        })
      );
    }
    setDashboardActions(roles);
  }, [permissions, dispatch]);

  //Showing Empty Grid when Loading
  const LoadingGrid = () => {
    return dashboardActions.map((v, i) => {
      return (
        <Grid item sm={6} md={4} lg={4} xl={3} key={i}>
          <Paper elevation={1} className={classes.paper}>
            <Box sx={{ mt: 2, mb: 2 }} className={classes.boxActionContainer}>
              <Skeleton className={classes.skeleton} variant="rectangular" width={"80%"} height={24} />
            </Box>
          </Paper>
        </Grid>
      );
    });
  };

  const breadcrumbArr = [{ link: "#", displayName: "Dashboard", active: true }];
  return (
    <CommonLayout title="Dashboard" breadcrumbArr={breadcrumbArr} loading={loading} autoScroll={true}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, ml: 0 }} className="main-contant-wrap">
        {/* For Roles other than Supplier (Supplier Role_id - 4) */}
        {role !== 4 && (
          <Box
            className="dashboard-head"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: role === 3 ? "space-between" : "flex-end"
            }}
          >
            {role === 3 && (
              <Box style={{ display: "flex", alignItems: "center" }}>
                <h3 style={{ margin: 0 }}>
                  Welcome back {UserName}, check below for price lists, job status, job creations and more...{" "}
                </h3>
              </Box>
            )}

            <Box style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }} >
              {role === 3 && (
                <>
                  {/* <Button
                    className="btn btn-primary-light"
                    variant="contained"
                    startIcon={<ReceiptLongIcon />}
                    onClick={() => navigate("/artwork-price-list")}
                  >
                    Artwork Price List
                  </Button> */}
                  <Button
                    className="btn btn-primary-light"
                    variant="contained"
                    startIcon={<ReceiptLongIcon />}
                    onClick={() => navigate("/products")}
                  >
                    Products Price List
                  </Button>
                </>
              )}
              {(role === 1 || (role === 2 && isSupplierDashboard === 1) || role === 3) &&
                <Button
                  className="btn btn-primary"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(role === 3 ? "/new-job" : "/admin/new-job")}
                >
                  Start New Job
                </Button>
              }
            </Box>
          </Box>
        )}

        <Grid container spacing={3} className="dashboard-page-box">
          {loading ? <LoadingGrid /> : <ActionGrid />}
        </Grid>
        <Typography style={{ fontSize: "22px", fontWeight: "400", marginBottom: 5, marginTop: 30 }}>
          Last 20 Job's
        </Typography>

        <Last20Jobs user={user.currentUser} isSupplierDashboard={isSupplierDashboard} />
      </Container>
    </CommonLayout>
  );
};

export default Dashboard;
